// components/common/Modal.jsx
import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { MODAL_THEME } from '../../config/theme.config';

/**
 * Reusable Modal Component with Default ScaleTime Branding
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal closes
 * @param {string|React.ReactNode} title - Modal header title
 * @param {string} subtitle - Optional subtitle text
 * @param {React.ReactNode} children - Modal body content
 * @param {React.ReactNode} footer - Optional footer content (buttons, etc)
 * @param {string} size - Modal size: 'sm', 'md', 'lg', 'xl', 'full' (default: 'lg')
 * @param {boolean} showCloseButton - Show X button in header (default: true)
 * @param {boolean} closeOnBackdropClick - Close when clicking outside (default: true)
 * @param {boolean} closeOnEscape - Close on ESC key (default: true)
 * @param {boolean} useGradientHeader - Use gradient header style (default: false)
 * @param {string} bodyClassName - Additional classes for body
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'lg',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  useGradientHeader = false,
  bodyClassName = '',
}) => {
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Determine header style based on gradient flag
  const headerClass = useGradientHeader 
    ? MODAL_THEME.header.gradient 
    : MODAL_THEME.header.default;
  
  const titleClass = useGradientHeader 
    ? MODAL_THEME.header.title.gradient 
    : MODAL_THEME.header.title.default;
  
  const subtitleClass = useGradientHeader 
    ? MODAL_THEME.header.subtitle.gradient 
    : MODAL_THEME.header.subtitle.default;
  
  const closeButtonClass = useGradientHeader 
    ? MODAL_THEME.header.closeButton.gradient 
    : MODAL_THEME.header.closeButton.default;

  // Use theme default body class if no custom class provided
  const bodyClass = bodyClassName || MODAL_THEME.body.default;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className={`bg-white rounded-lg shadow-xl ${MODAL_THEME.sizes[size]} w-full max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className={`px-6 py-4 flex items-center justify-between flex-shrink-0 ${headerClass}`}>
            <div className="flex-1 min-w-0">
              {title && (
                <h2 className={titleClass}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className={subtitleClass}>
                  {subtitle}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button 
                onClick={onClose} 
                className={`ml-4 flex-shrink-0 ${closeButtonClass}`}
                aria-label="Close modal"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className={bodyClass}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className={MODAL_THEME.footer.default}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;